import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import {
  FaFileAudio,
  FaMicrophone,
  FaPause,
  FaPlay,
  FaYoutube,
} from "react-icons/fa";
import relativeTime from "dayjs/plugin/relativeTime";
import { formatSeconds } from "@jammable/ui-components/AudioPlayer/utils";
import ReactPlayer from "react-player";
import Image from "next/image";

/**
 * Calculate optimal preview start and end times based on track duration
 * @param {number} duration - Total duration of the track in seconds
 * @param {number} previewLength - Desired length of preview in seconds (default: 15)
 * @returns {Object} - { startTime, endTime }
 */
const calculatePreviewTimes = (duration, previewLength = 15) => {
  if (!duration || duration <= 0) {
    return { startTime: 0, endTime: previewLength };
  }

  if (duration <= previewLength * 2) {
    return { startTime: 0, endTime: Math.min(duration, previewLength) };
  }

  const targetPosition = duration * 0.4;

  let startTime = Math.max(0, targetPosition - previewLength * 0.3);
  let endTime = startTime + previewLength;

  if (endTime > duration) {
    endTime = duration;
    startTime = Math.max(0, endTime - previewLength);
  }

  return {
    startTime: Math.round(startTime),
    endTime: Math.round(endTime),
  };
};

const RecentFile = ({ file, mode, isPlaying, onPlayPause, onSelect }) => {
  dayjs.extend(relativeTime);
  const [loading, setLoading] = useState(false);
  const [progress, setProgressLocal] = useState(0);
  const playerRef = useRef(null);
  const [hasWindow, setHasWindow] = useState(false);
  const lastUpdateTimeRef = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHasWindow(true);
    }
  }, []);

  // Calculate preview times based on file duration
  const { startTime, endTime } = calculatePreviewTimes(file?.duration);
  const previewDuration = endTime - startTime;
  const previewUrl = `${file?.urls?.base}#t=${startTime},${endTime}`;

  const handleProgress = () => {
    // Throttle updates
    if (
      isPlaying &&
      playerRef.current &&
      Date.now() - lastUpdateTimeRef.current > 250
    ) {
      const currentTime = playerRef.current.getCurrentTime() || 0;
      // Calculate progress based on current time within the preview window
      // Revert to using currentTime and startTime
      const adjustedTime = Math.max(
        0,
        Math.min(previewDuration, currentTime - startTime),
      );
      const progressPercentage =
        previewDuration > 0 ? (adjustedTime / previewDuration) * 100 : 0;

      setProgressLocal(progressPercentage);
      lastUpdateTimeRef.current = Date.now();
    }
  };

  const handlePlaybackEnd = () => {
    onPlayPause();
    setProgressLocal(0);
  };

  return (
    <div
      onClick={() => {
        setLoading(true);

        // Construct details object
        const fileDetails = {
          url: file.urls?.base, // Assuming base URL is the one to use
          title: file.metadata?.title || file.fileName || "Untitled File",
          metadata: file.metadata, // Pass along metadata
          _id: file._id,
        };

        // Basic validation
        if (!fileDetails.url || !fileDetails.title) {
          console.error("Missing URL or Title for selected file:", file);
          // toast.error("Could not select this file. Missing details.");
          setLoading(false);
          return;
        }

        // Simulate delay (optional)
        setTimeout(() => {
          if (onSelect) {
            onSelect(fileDetails); // Call the new callback
          }
          // Old prop calls removed:
          // setUploadedFileId(file._id);
          // setFinalUrl(file.urls.base);
          // setProgress(100);
          // setTitle(file.metadata?.title ?? file.fileName);
          // nextStage();
          setLoading(false);
        }, Math.random() * 500); // Shorter delay example
      }}
      className={`tw-flex tw-w-full ${mode === "dark" && "tw-bg-purple-700/40"} tw-justify-between ${mode === "dark" ? "hover:tw-bg-purple-700/60" : "hover:tw-bg-gray-50"} ${loading && "tw-opacity-50"} tw-items-center tw-gap-3 tw-transition-all tw-cursor-pointer tw-p-3 tw-rounded-2xl ${mode === "dark" ? "tw-border-0" : "tw-border"} tw-border-solid ${mode === "dark" ? "tw-border-white/40" : "tw-border-black/10"}`}
    >
      <div className="tw-flex tw-gap-2 tw-items-center">
        <div
          onClick={(e) => {
            e.stopPropagation();
            onPlayPause();
          }}
          className="tw-relative tw-w-10 tw-min-w-10 tw-min-h-10 tw-h-10 tw-bg-purple-200 tw-rounded-full"
        >
          <svg
            className="tw-w-full tw-h-full tw-overflow-visible"
            viewBox="0 0 40 40"
          >
            <circle
              className="tw-text-purple-700"
              strokeWidth="3"
              stroke="currentColor"
              fill="transparent"
              r="19"
              cx="20"
              cy="20"
              strokeDasharray={2 * Math.PI * 19}
              strokeDashoffset={(2 * Math.PI * 19 * (100 - progress)) / 100}
              transform="rotate(-90 20 20)"
            />
          </svg>
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-text-sm tw-justify-center tw-rounded-full">
            {isPlaying ? (
              <FaPause className="tw-text-purple-700" />
            ) : (
              <FaPlay className="tw-text-purple-700 tw-ml-1" />
            )}
          </div>
        </div>
        {file.metadata?.image?.url ? (
          <Image
            src={file.metadata.image.url}
            width={64}
            height={64}
            className="tw-rounded-xl"
            style={{
              objectFit: "cover",
            }}
            alt={file?.metadata?.title ?? file.fileName ?? "Song Cover"}
          />
        ) : (
          <div className="tw-size-16 tw-aspect-square tw-flex tw-text-black/50 tw-items-center tw-justify-center tw-rounded-xl tw-bg-gray-100">
            {file.type === "youtube" ? (
              <FaYoutube size={20} />
            ) : file.type === "microphone" ? (
              <FaMicrophone size={18} />
            ) : (
              <FaFileAudio size={20} />
            )}
          </div>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-text-left tw-truncate tw-flex-1 tw-min-w-0">
        <div
          className={`tw-text-sm tw-font-black ${mode === "dark" ? "tw-text-white/50" : "tw-text-black/50"} tw-truncate`}
        >
          {dayjs(file.createdAt).fromNow()}
        </div>
        <div className="tw-font-black tw-max-w-[225px] sm:tw-max-w-[300px] md:tw-max-w-max tw-truncate md:tw-text-lg">
          {file?.metadata?.title ?? file.fileName}
        </div>
        <div
          className={`tw-text-sm tw-font-black  tw-max-w-[225px] sm:tw-max-w-[300px] tw-truncate  ${mode === "dark" ? "tw-text-white/50" : "tw-text-black/50"} tw-truncate`}
        >
          {file.uses ?? 0} {file.uses === 1 ? "Use" : "Uses"}
          {file.duration && ` · ${formatSeconds(file.duration)}`}{" "}
          {file?.metadata?.artist && ` · ${file.metadata.artist}`}
        </div>
      </div>
      {hasWindow && isPlaying && (
        <ReactPlayer
          style={{ display: "none" }}
          onProgress={handleProgress}
          ref={playerRef}
          url={previewUrl}
          playing={isPlaying}
          onEnded={handlePlaybackEnd}
          config={{
            file: {
              attributes: {
                preload: "metadata",
              },
              forceAudio: true,
            },
          }}
        />
      )}
    </div>
  );
};

export default RecentFile;
